import { collection, addDoc, getDocs, query, where, doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { getFirestore } from 'firebase/firestore';

export const contactsService = {
  async addContact(contactData) {
    try {
      const docRef = await addDoc(collection(db, 'contacts'), {
        ...contactData,
        createdAt: new Date().toISOString(),
      });
      return { id: docRef.id, ...contactData };
    } catch (error) {
      console.error('Error adding contact:', error);
      throw error;
    }
  },

  async getContacts(type = null) {
    try {
      const contactsRef = collection(db, 'contacts');
      let q;
      
      // Only apply type filter if type parameter is provided
      if (type) {
        q = query(contactsRef, where('contactType', '==', type));
      } else {
        q = query(contactsRef); // Get all contacts if no type specified
      }
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting contacts:', error);
      throw error;
    }
  },

  async getContactDetails(id) {
    try {
      const docRef = doc(db, 'contacts', id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Contact not found');
      }

      const contactData = {
        id: docSnap.id,
        ...docSnap.data(),
      };

      // Fetch all transactions for this contact
      const transactionsRef = collection(db, 'transactions');
      const q = query(transactionsRef, where('contactId', '==', id));
      const transactionsSnap = await getDocs(q);
      
      const transactions = [];
      let totalPurchases = 0;
      let totalCratesOut = 0;
      let totalPendingPayments = 0;

      transactionsSnap.forEach((doc) => {
        const transaction = {
          id: doc.id,
          ...doc.data(),
        };
        transactions.push(transaction);
        
        // Calculate totals
        if (transaction.amount) totalPurchases += transaction.amount;
        if (transaction.cratesGiven) totalCratesOut += transaction.cratesGiven;
        if (transaction.status === 'Pending') totalPendingPayments += transaction.amount;
      });

      // Calculate monthly averages
      const monthlyData = transactions.reduce((acc, transaction) => {
        const date = new Date(transaction.date);
        const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`;
        if (!acc[monthYear]) acc[monthYear] = 0;
        acc[monthYear] += transaction.amount || 0;
        return acc;
      }, {});

      const monthlyAverage = Object.values(monthlyData).reduce((a, b) => a + b, 0) / 
                            (Object.keys(monthlyData).length || 1);

      return {
        ...contactData,
        transactions,
        stats: {
          totalPurchases,
          monthlyAverage,
          totalCratesOut,
          totalPendingPayments,
          transactionCount: transactions.length
        }
      };
    } catch (error) {
      console.error('Error fetching contact details:', error);
      throw error;
    }
  },

  async updateContact(id, contactData) {
    const db = getFirestore();
    const batch = writeBatch(db);
    
    // Update contact document
    const contactRef = doc(db, 'contacts', id);
    batch.update(contactRef, {
      ...contactData,
      updatedAt: new Date().toISOString()
    });

    // If name is being updated, update related collections
    if (contactData.name) {
      const isCustomer = contactData.contactType === 'customer';
      const collectionName = isCustomer ? 'orders' : 'purchases';
      const nameField = isCustomer ? 'customerName' : 'supplierName';
      
      // Get all related documents
      const q = query(
        collection(db, collectionName), 
        where(isCustomer ? 'customerId' : 'supplierId', '==', id)
      );
      
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          [nameField]: contactData.name
        });
      });
    }

    // Commit all updates
    await batch.commit();

    // Return updated contact data
    const updatedContact = await getDoc(contactRef);
    return { id: updatedContact.id, ...updatedContact.data() };
  },

  async getContact(contactId) {
    try {
      const contactDoc = await getDoc(doc(db, 'contacts', contactId));
      if (!contactDoc.exists()) {
        throw new Error('Contact not found');
      }
      return {
        id: contactDoc.id,
        ...contactDoc.data()
      };
    } catch (error) {
      console.error('Error fetching contact:', error);
      throw error;
    }
  },

  async createContact(contactData) {
    try {
      const contactsRef = collection(db, 'contacts');
      const docRef = await addDoc(contactsRef, {
        ...contactData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      // Fetch the newly created contact to return it with the ID
      const newContact = await this.getContact(docRef.id);
      return newContact;
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  },

  async getAllContacts() {
    const querySnapshot = await getDocs(collection(db, 'contacts'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },

  async getCustomers() {
    const q = query(
      collection(db, 'contacts'),
      where('contactType', '==', 'customer')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
};